import apiClient from './api';
import * as XLSX from 'xlsx';
import { API_ENDPOINTS } from '@/config/api.config';
import type {
  CoursePerformanceMetricsResponse,
  CoursePerformanceListResponse,
  CoursePerformanceResponse,
  CoursePerformanceListParams,
  CoursePerformanceMetricsParams,
  CoursePerformanceMetricsDto,
  CoursePerformanceListResponseDto,
  CoursePerformanceItemDto,
  Granularity,
  CourseUsersListParams,
  CourseDataResponse,
  CourseDataResponses,
  CourseUserData,
  CourseUserAnalytics,
  CourseUserAnalyticsResponse
} from '@/types/course-performance.types';

/**
 * Service for managing course performance analytics
 * Provides metrics, list views, and detailed course performance data
 */
class CoursePerformanceService {
  /**
   * Get high-level course performance metrics
   * Returns total active courses, enrollments, and average completion rate
   * @param params - Optional granularity parameter for time-based filtering
   * @returns Promise with course performance metrics
   */
  async getMetrics(params?: CoursePerformanceMetricsParams): Promise<CoursePerformanceMetricsDto> {
    try {
      const body: any = {};

      if (params?.granularity) {
        body.granularity = params.granularity;
      }
      if (params?.start_date) {
        body.start_date = params.start_date;
      }
      if (params?.end_date) {
        body.end_date = params.end_date;
      }

      const response = await apiClient.post<CoursePerformanceMetricsResponse>(
        API_ENDPOINTS.COURSE_PERFORMANCE.METRICS,
        body
      );

      if (!response.data.success) {
        throw new Error(response.data.description || 'Failed to fetch course performance metrics');
      }

      return response.data.data;
    } catch (error) {
      console.error('Error fetching course performance metrics:', error);
      throw error;
    }
  }

  /**
   * Get paginated list of course performance data with filtering and sorting
   * @param params - Query parameters for filtering, sorting, and pagination
   * @returns Promise with paginated course performance list
   */
  async getCoursePerformanceList(params?: CoursePerformanceListParams): Promise<CoursePerformanceListResponseDto> {
    try {
      const body: any = {};

      // Add parameters to body if provided
      if (params?.page !== undefined) {
        body.page = params.page;
      }
      if (params?.limit !== undefined) {
        body.limit = params.limit;
      }
      if (params?.status) {
        body.status = params.status;
      }
      if (params?.search) {
        body.search = params.search;
      }
      if (params?.sortBy) {
        body.sortBy = params.sortBy;
      }
      if (params?.sortOrder) {
        body.sortOrder = params.sortOrder;
      }
      if (params?.granularity) {
        body.granularity = params.granularity;
      }
      if (params?.start_date) {
        body.start_date = params.start_date;
      }
      if (params?.end_date) {
        body.end_date = params.end_date;
      }
      if (params?.groupKey) {
        body.groupKey = params.groupKey;
      }
      if (params?.roleKey) {
        body.roleKey = params.roleKey;
      }
      if (params?.categoryId) {
        body.categoryId = params.categoryId;
      }

      const response = await apiClient.post<CoursePerformanceListResponse>(
        API_ENDPOINTS.COURSE_PERFORMANCE.LIST,
        body
      );

      if (!response.data.success) {
        throw new Error(response.data.description || 'Failed to fetch course performance list');
      }

      return response.data.data;
    } catch (error) {
      console.error('Error fetching course performance list:', error);
      throw error;
    }
  }
/**
 * Get user by course_id
 */
 async getCourseUsers(courseId: string, params: CourseUsersListParams): Promise<CourseDataResponses> {
    try {
        const body: any = {};
        if (params.page) body.page = params.page;
        if (params.limit) body.limit = params.limit;
        if (params.search) body.search = params.search;
        if (params.status) body.status_filter = params.status;

        const url = API_ENDPOINTS.COURSE_PERFORMANCE.GET_USER_BASED_ON_COURSE(courseId);
        const response = await apiClient.post<CourseDataResponses>(url, body);

        if (!response.data.success) {
            throw new Error(response.data.description || 'Failed to fetch course users');
        }

        return response.data;
    } catch (error) {
        console.error(`Error fetching users for course ID ${courseId}:`, error);
        throw error;
    }
}

  /**
   * Get course user analytics including total assigned users, completed count, and average progress
   * @param courseId - The unique identifier of the course
   * @returns Promise with course user analytics
   */
  async getCourseUserAnalytics(courseId: string): Promise<CourseUserAnalytics> {
    try {
      const url = API_ENDPOINTS.COURSE_PERFORMANCE.GET_ANALYTICS(courseId);
      const response = await apiClient.get<CourseUserAnalyticsResponse>(url);

      if (!response.data.success) {
        throw new Error(response.data.description || 'Failed to fetch course user analytics');
      }

      return response.data.data;
    } catch (error) {
      console.error(`Error fetching analytics for course ID ${courseId}:`, error);
      throw error;
    }
  }
  /**
   * Get performance data for a specific course
   * @param courseId - The unique identifier of the course
   * @param granularity - Optional time granularity for filtering
   * @returns Promise with course performance data or null if not found
   */
  async getCoursePerformanceById(courseId: string, granularity?: Granularity): Promise<CoursePerformanceItemDto | null> {
    try {
      const queryParams = granularity ? `?granularity=${granularity}` : '';
      const response = await apiClient.get<CoursePerformanceResponse>(
        `${API_ENDPOINTS.COURSE_PERFORMANCE.GET_BY_ID(courseId)}${queryParams}`
      );

      if (!response.data.success) {
        // Check if it's a 404 (course not found)
        if (response.data.code === 404) {
          return null;
        }
        throw new Error(response.data.description || 'Failed to fetch course performance');
      }

      return response.data.data;
    } catch (error) {
      console.error(`Error fetching course performance for ID ${courseId}:`, error);
      throw error;
    }
  }

  /**
   * Search courses by name
   * @param searchTerm - The search term to filter courses by name
   * @param limit - Maximum number of results to return
   * @returns Promise with filtered course list
   */
  async searchCourses(searchTerm: string, limit: number = 20): Promise<CoursePerformanceListResponseDto> {
    return this.getCoursePerformanceList({
      search: searchTerm,
      limit,
      page: 1,
      sortBy: 'name',
      sortOrder: 'asc'
    });
  }

  /**
   * Get top performing courses by completion rate
   * @param limit - Number of top courses to return
   * @returns Promise with top performing courses
   */
  async getTopPerformingCourses(limit: number = 10): Promise<CoursePerformanceItemDto[]> {
    const result = await this.getCoursePerformanceList({
      status: 'active',
      sortBy: 'completion',
      sortOrder: 'desc',
      limit,
      page: 1
    });

    return result.courses;
  }

  /**
   * Get courses with low completion rates that may need attention
   * @param threshold - Completion percentage threshold (default 50%)
   * @param limit - Maximum number of courses to return
   * @returns Promise with courses needing attention
   */
  async getCoursesNeedingAttention(threshold: number = 50, limit: number = 20): Promise<CoursePerformanceItemDto[]> {
    const result = await this.getCoursePerformanceList({
      status: 'active',
      sortBy: 'completion',
      sortOrder: 'asc',
      limit,
      page: 1
    });

    // Filter courses below the threshold
    return result.courses.filter(course => course.completion_percentage < threshold);
  }

  /**
   * Get most popular courses by enrollment
   * @param limit - Number of courses to return
   * @returns Promise with most enrolled courses
   */
  async getMostEnrolledCourses(limit: number = 10): Promise<CoursePerformanceItemDto[]> {
    const result = await this.getCoursePerformanceList({
      status: 'active',
      sortBy: 'enrollments',
      sortOrder: 'desc',
      limit,
      page: 1
    });

    return result.courses;
  }

  /**
   * Get courses by status
   * @param status - Course status to filter by
   * @param page - Page number for pagination
   * @param limit - Items per page
   * @returns Promise with filtered course list
   */
  async getCoursesByStatus(
    status: 'active' | 'inactive' | 'completed' | 'draft',
    page: number = 1,
    limit: number = 20
  ): Promise<CoursePerformanceListResponseDto> {
    return this.getCoursePerformanceList({
      status,
      page,
      limit,
      sortBy: 'name',
      sortOrder: 'asc'
    });
  }

  /**
   * Calculate overall learning effectiveness score
   * Based on completion rates, scores, and satisfaction
   * @returns Promise with effectiveness metrics
   */
  async calculateLearningEffectiveness(): Promise<{
    effectiveness_score: number;
    metrics: CoursePerformanceMetricsDto;
    insights: string[];
  }> {
    try {
      const metrics = await this.getMetrics();
      const activeCourses = await this.getCoursesByStatus('active', 1, 100);
      
      // Calculate effectiveness score (0-100)
      let effectivenessScore = 0;
      let insights: string[] = [];

      // Factor 1: Completion rate (40% weight)
      const completionScore = metrics.avg_completion_rate * 0.4;
      effectivenessScore += completionScore;

      if (metrics.avg_completion_rate > 80) {
        insights.push('Excellent completion rates across courses');
      } else if (metrics.avg_completion_rate < 50) {
        insights.push('Low completion rates indicate potential engagement issues');
      }

      // Factor 2: Average satisfaction (30% weight)
      if (activeCourses.courses.length > 0) {
        const avgSatisfaction = activeCourses.courses.reduce(
          (sum, course) => sum + course.satisfaction, 0
        ) / activeCourses.courses.length;
        const satisfactionScore = (avgSatisfaction / 5) * 100 * 0.3;
        effectivenessScore += satisfactionScore;

        if (avgSatisfaction >= 4) {
          insights.push('High learner satisfaction indicates quality content');
        } else if (avgSatisfaction < 3) {
          insights.push('Low satisfaction scores suggest content improvements needed');
        }
      }

      // Factor 3: Engagement (30% weight)
      if (metrics.total_active_courses > 0 && metrics.total_enrollments > 0) {
        const engagementRatio = Math.min(
          metrics.total_enrollments / (metrics.total_active_courses * 10), 
          1
        );
        const engagementScore = engagementRatio * 100 * 0.3;
        effectivenessScore += engagementScore;

        if (metrics.total_enrollments / metrics.total_active_courses > 20) {
          insights.push('Strong enrollment numbers show high course demand');
        }
      }

      return {
        effectiveness_score: Math.round(effectivenessScore),
        metrics,
        insights
      };
    } catch (error) {
      console.error('Error calculating learning effectiveness:', error);
      throw error;
    }
  }

  /**
   * Export course performance data to CSV/Excel format
   * @param format - Export format ('csv' or 'excel')
   * @param filters - Optional filters to apply
   * @returns Promise with export data
   */
  async exportCoursePerformance(
    format: 'csv' | 'excel' = 'csv',
    filters?: CoursePerformanceListParams
  ): Promise<Blob> {
    try {
      // Get all courses with applied filters
      const data = await this.getCoursePerformanceList({
        ...filters,
        limit: 1000 // Get maximum records for export
      });

      // Convert to CSV format
      if (format === 'csv') {
        const csvContent = this.convertToCSV(data.courses);
        return new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      }

      // For Excel format, would need additional library like xlsx
      throw new Error('Excel export not yet implemented');
    } catch (error) {
      console.error('Error exporting course performance:', error);
      throw error;
    }
  }

 async exportCourseUserData(
    courseId: string,
    format: 'csv' | 'excel',
    filters?: { search?: string; status?: string; courseName?: string }
  ): Promise<{ success: boolean; filename: string; count: number; message?: string }> {
    const body: any = {};
    if (filters?.search) body.search = filters.search;
    if (filters?.status) body.status_filter = filters.status;

    const url = API_ENDPOINTS.EXPORT.COURSE_USER_DATA(courseId);
    const response = await apiClient.post(url, body);

    const data = response.data?.data;
    const users: CourseUserData[] = data?.users || [];

    if (users.length === 0) {
      return {
        success: false,
        filename: '',
        count: 0,
        message: 'No user data available to export with the current filters'
      };
    }

    const filename = this.generateCourseUserDataFilename(format, filters);

    if (format === 'excel') {
      const blob = this.generateCourseUserDataExcel(users);
      this.downloadExcelBlob(blob, filename);
    } else {
      const csv = this.generateCourseUserDataCSV(users);
      this.downloadCSVContent(csv, filename);
    }

    return { success: true, filename, count: users.length };
  }

  private generateCourseUserDataFilename(
    format: 'csv' | 'excel',
    filters?: { search?: string; status?: string; courseName?: string }
  ): string {
    const timestamp = new Date().toISOString().split('T')[0];
    const parts = ['course-user-data'];

    if (filters?.courseName) {
      const sanitized = filters.courseName.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      parts.push(sanitized);
    }
    if (filters?.status && filters.status !== 'all') {
      parts.push(`status-${filters.status}`);
    }
    if (filters?.search) {
      const sanitized = filters.search.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      parts.push(`search-${sanitized}`);
    }
    parts.push(timestamp);

    const ext = format === 'excel' ? 'xlsx' : 'csv';
    return parts.join('_') + `.${ext}`;
  }

  private formatSecondsToHHMMSS(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }

  private generateCourseUserDataCSV(users: CourseUserData[]): string {
    const headers = ['First Name', 'Last Name', 'Email', 'Status', 'Progress (%)', 'Time Spent (HH:MM:SS)'];
    const escape = (val: string | number) => {
      const s = String(val);
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = users.map(u => [
      escape(u.given_name),
      escape(u.family_name),
      escape(u.email),
      escape(u.status.replace(/_/g, ' ')),
      escape(u.progress_percentage),
      escape(this.formatSecondsToHHMMSS(u.total_time_spent_seconds)),
    ].join(','));
    return [headers.join(','), ...rows].join('\n');
  }

  private generateCourseUserDataExcel(users: CourseUserData[]): Blob {
    const data = users.map(u => ({
      'First Name': u.given_name,
      'Last Name': u.family_name,
      'Email': u.email,
      'Status': u.status.replace(/_/g, ' '),
      'Progress (%)': u.progress_percentage,
      'Time Spent (HH:MM:SS)': this.formatSecondsToHHMMSS(u.total_time_spent_seconds),
    }));
    const worksheet = XLSX.utils.json_to_sheet(data);
    worksheet['!cols'] = [
      { wch: 15 }, { wch: 15 }, { wch: 30 }, { wch: 15 }, { wch: 12 }, { wch: 20 },
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Course Users');
    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  }

  private downloadCSVContent(content: string, filename: string) {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  private downloadExcelBlob(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Convert course data to CSV format
   * @param courses - Array of course performance data
   * @returns CSV string
   */
  private convertToCSV(courses: CoursePerformanceItemDto[]): string {
    const headers = [
      'Course ID',
      'Course Name',
      'Status',
      'Total Enrollments',
      'Active Enrollments',
      'Completion Rate (%)',
      'Average Score'
      // 'Satisfaction Rating' - Removed to match UI changes
    ];

    const rows = courses.map(course => [
      course.course_id,
      `"${course.course_name}"`, // Quote to handle commas in names
      course.status,
      course.total_user_enrollments,
      course.total_user_active_enrollments,
      course.completion_percentage.toFixed(2),
      course.avg_score.toFixed(2)
      // course.satisfaction.toFixed(1) - Removed to match UI changes
    ]);

    return [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
  }
}

// Export singleton instance
export const coursePerformanceService = new CoursePerformanceService();